import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiTag, FiFileText } = FiIcons;

const AddNote = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { addNote, members } = useData();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const member = members.find(m => String(m.id) === String(memberId));

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'Performance'
  });

  const categories = [
    'Performance',
    'Areas for Improvement',
    'Mindset',
    'Strategy',
    'Marketing',
    'Technical',
    'General'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!memberId || !user) return;
    
    setLoading(true);

    try {
      await addNote({
        member_id: memberId,
        coach_id: user.id,
        coach_name: user.name,
        ...formData
      });
      navigate(`/members/${memberId}`);
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!member) {
    return <div className="p-6">Member not found</div>;
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add Session Note</h1>
            <p className="text-gray-600">Recording session for {member.name}</p>
          </div>
          <Link
            to={`/members/${memberId}`}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
            Cancel
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Title / Topic *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Product Research Review"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <div className="relative">
                <SafeIcon icon={FiTag} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Notes *
              </label>
              <div className="relative">
                <SafeIcon icon={FiFileText} className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  required
                  rows={10}
                  placeholder="Record detailed notes from the coaching session..."
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <Link
                to={`/members/${memberId}`}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} className="w-5 h-5" />
                    <span>Save Note</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AddNote;